"use client";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useState, useMemo, useEffect } from "react";
import arrowDownIcon from "@/assets/images/arrow_down.svg";
import viugoFilterFlameIcon from "@/assets/images/viugo/kbv-v16-filter-flame-icon.svg";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";
const THU_VI = [
  "Chủ nhật",
  "Thứ hai",
  "Thứ ba",
  "Thứ tư",
  "Thứ năm",
  "Thứ sáu",
  "Thứ bảy",
];

export default function SinDateTabs({
  selectedDate,
  onDateChange,
  compact = false,
  isHotOnly = false,
  onToggleHot,
}) {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const today = useMemo(() => dayjs().tz(TZ), []);
  const todayString = today.format("YYYY-MM-DD");

  const dateOptions = useMemo(() => {
    const options = [];

    options.push({
      value: "live",
      label: "Trực tiếp",
      dateLabel: null,
      shortDate: null,
    });

    options.push({
      value: todayString,
      label: "Hôm nay",
      dateLabel: today.format("DD/MM"),
      shortDate: today.format("DD-MM"),
    });

    const tomorrow = today.clone().add(1, "day");
    options.push({
      value: tomorrow.format("YYYY-MM-DD"),
      label: "Ngày mai",
      dateLabel: tomorrow.format("DD/MM"),
      shortDate: tomorrow.format("DD-MM"),
    });

    for (let i = 2; i <= 6; i++) {
      const d = today.clone().add(i, "day");
      options.push({
        value: d.format("YYYY-MM-DD"),
        label: THU_VI[d.day()],
        dateLabel: d.format("DD/MM"),
        shortDate: d.format("DD-MM"),
      });
    }

    return options;
  }, [todayString, today]);

  const selectedOpt = useMemo(() => {
    return dateOptions.find((o) => o.value === selectedDate) || dateOptions[0];
  }, [dateOptions, selectedDate]);

  // Prevent scrolling when popup is open
  useEffect(() => {
    if (isPopupOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isPopupOpen]);

  return (
    <div className="flex w-full items-center">
      {compact ? (
        <>
        <div 
          onClick={(e) => {
            e.stopPropagation();
            setIsPopupOpen(true);
          }}
          className="flex h-[42px] grow items-center justify-between rounded-none border border-solid border-[var(--theme-border)] bg-[var(--theme-foreground)] px-[10px] text-[14px] relative cursor-pointer"
        >
          <div className="flex flex-row items-center gap-x-1 leading-tight">
            <span className="font-bold text-[var(--theme-text)]">{selectedOpt.label}</span>
            {selectedOpt.dateLabel && (
              <span className="text-[14px] text-[var(--theme-text)]">({selectedOpt.dateLabel})</span>
            )}
          </div>
          <img src={arrowDownIcon} className="sin-icon-arrow h-[14px] w-[14px]" alt="" />
        </div>

        {typeof onToggleHot === "function" ? (
          <button
            type="button"
            onClick={onToggleHot}
            aria-pressed={isHotOnly}
            className={`sin-date-tab ml-2 inline-flex h-[42px] shrink-0 items-center justify-center gap-1 rounded-[8px] px-3 text-[13px] transition-all ${
              isHotOnly ? "sin-date-tab--selected font-bold" : "font-semibold"
            }`}
          >
            <img
              src={viugoFilterFlameIcon}
              alt=""
              width={18}
              height={18}
              className="shrink-0"
              aria-hidden
            />
            <span>Trận hot</span>
          </button>
        ) : null}
        </>
      ) : (
        <div className="flex w-full min-w-0 items-center gap-2">
          <div className="flex h-full min-w-0 flex-1 gap-2 overflow-hidden px-1 pb-1.5 items-center">
          {dateOptions.map((opt) => {
            const isSelected = !isHotOnly && selectedDate === opt.value;
            const tabClass =
              "sin-date-tab h-[56px] min-w-0 flex-1 basis-0 cursor-pointer rounded-[8px] flex flex-col items-center justify-center text-[14px] text-center leading-tight px-0.5";

            if (isSelected) {
              return (
                <div
                  key={opt.value}
                  onClick={() => onDateChange(opt.value)}
                  className={`${tabClass} sin-date-tab--selected font-bold`}
                >
                  <span className="w-full truncate">{opt.label}</span>
                  {opt.dateLabel && (
                    <span className="w-full truncate font-normal opacity-70">{opt.dateLabel}</span>
                  )}
                </div>
              );
            }

            return (
              <div
                key={opt.value}
                onClick={() => onDateChange(opt.value)}
                className={`${tabClass} transition-all`}
              >
                <span className="w-full truncate font-bold">{opt.label}</span>
                {opt.dateLabel && (
                  <span className="w-full truncate font-normal">{opt.dateLabel}</span>
                )}
              </div>
            );
          })}
          </div>
          {typeof onToggleHot === "function" ? (
            <button
              type="button"
              onClick={onToggleHot}
              aria-pressed={isHotOnly}
              className={`sin-date-tab mr-1 inline-flex h-[56px] shrink-0 items-center justify-center gap-1 rounded-[8px] px-3 text-[14px] transition-all ${
                isHotOnly ? "sin-date-tab--selected font-bold" : "font-semibold"
              }`}
            >
              <img
                src={viugoFilterFlameIcon}
                alt=""
                width={20}
                height={20}
                className="shrink-0"
                aria-hidden
              />
              <span>Trận hot</span>
            </button>
          ) : null}
        </div>
      )}

      {/* Mobile Date Picker Popup */}
      <div className={`fixed inset-0 z-[999] transition-opacity duration-300 pointer-events-none ${isPopupOpen ? "opacity-100 pointer-events-auto" : "opacity-0"}`}>
        {/* Backdrop */}
        <div 
          onClick={(e) => {
            e.stopPropagation();
            setIsPopupOpen(false);
          }}
          className="absolute inset-0 bg-[var(--theme-overlay)]" 
          style={{ backdropFilter: "blur(8px)" }}
        />
        
        {/* Bottom Sheet Popup */}
        <div 
          className={`absolute bottom-0 left-0 right-0 w-full transition-transform duration-300 ease-out transform ${isPopupOpen ? "translate-y-0" : "translate-y-full"}`}
          style={{ zIndex: 1000 }}
        >
          <div className="w-full kbv-sin-card shadow-[0_-4px_6px_rgba(0,0,0,0.1)] rounded-none overflow-hidden pb-10">
            <div className="relative flex items-center justify-center">
              <div className="text-[14px] py-4">Chọn Ngày</div>
              <div 
                onClick={(e) => {
                  e.stopPropagation();
                  setIsPopupOpen(false);
                }}
                className="absolute right-2 top-2 cursor-pointer"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect width="24" height="24" rx="4" transform="matrix(-1 0 0 1 24 0)" fill="var(--theme-text-muted)"></rect>
                  <path d="M16 8L8 16" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                  <path d="M8 8L16 16" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
              </div>
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {dateOptions.map((opt) => {
                const isSelected = !isHotOnly && selectedDate === opt.value;
                return (
                  <div 
                    key={opt.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDateChange(opt.value);
                      setIsPopupOpen(false);
                    }}
                    className={`sin-date-popup-option flex h-[38px] items-center gap-2 px-6 transition-colors ${isSelected ? "bg-[var(--theme-accent)]" : ""}`}
                  >
                    <div className="text-[14px] leading-6 flex items-center">
                      <span className={`sin-date-popup-label ${opt.shortDate ? "w-[66px]" : ""} inline-block`}>{opt.label}</span>
                      {opt.shortDate && (
                        <>
                          <span className="sin-date-popup-label hidden md:inline-block text-[12px] opacity-70 ml-2">{opt.shortDate}</span>
                          <span className="sin-date-popup-label md:hidden text-[14px] ml-1 font-normal">({opt.shortDate})</span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
